<script setup>
import { computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useThemeColors } from 'dashboard/composables/useThemeColors';
import BarChart from 'shared/components/charts/BarChart.vue';

// eslint-disable-next-line no-unused-vars
const store = useStore();
const agentSummaryData = useMapGetter('summaryReports/getAgentSummaryReports');
const { getChartColors } = useThemeColors();

const chartData = computed(() => {
  const dataToUse = agentSummaryData.value;

  if (!dataToUse || dataToUse.length === 0) {
    return { labels: [], datasets: [] };
  }

  const totalConversations = dataToUse.reduce((sum, agent) => {
    return sum + (agent.conversationsCount || 0);
  }, 0);

  const totalMessages = dataToUse.reduce((sum, agent) => {
    return sum + (agent.totalMessagesCount || 0);
  }, 0);

  if (totalConversations === 0 && totalMessages === 0) {
    return { labels: [], datasets: [] };
  }

  const agentsWithData = dataToUse
    .filter(
      agent =>
        (agent.conversationsCount || 0) > 0 ||
        (agent.totalMessagesCount || 0) > 0
    )
    .map(agent => ({
      name: agent.name || 'Agente sem nome',
      conversationPercentage:
        totalConversations > 0
          ? Math.round(
              ((agent.conversationsCount || 0) / totalConversations) * 100
            )
          : 0,
      messagePercentage:
        totalMessages > 0
          ? Math.round(((agent.totalMessagesCount || 0) / totalMessages) * 100)
          : 0,
      totalScore:
        (agent.conversationsCount || 0) + (agent.totalMessagesCount || 0),
    }))
    .sort((a, b) => b.totalScore - a.totalScore);

  const chartColors = getChartColors();

  return {
    labels: agentsWithData.map(agent => agent.name),
    datasets: [
      {
        label: 'Atendimentos',
        data: agentsWithData.map(agent => agent.conversationPercentage),
        backgroundColor: chartColors.chartPalette[0],
        borderColor: chartColors.chartPalette[0],
        borderWidth: 1,
      },
      {
        label: 'Mensagens',
        data: agentsWithData.map(agent => agent.messagePercentage),
        backgroundColor: chartColors.chartPalette[1],
        borderColor: chartColors.chartPalette[1],
        borderWidth: 1,
      },
    ],
  };
});

const chartOptions = computed(() => {
  const chartColors = getChartColors();

  return {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          pointStyle: 'rect',
          color: chartColors.textPrimary,
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        backgroundColor: chartColors.tooltipBg,
        titleColor: chartColors.textPrimary,
        bodyColor: chartColors.textSecondary,
        borderColor: chartColors.tooltipBorder,
        borderWidth: 1,
        callbacks: {
          label: context => {
            const label = context.dataset.label || '';
            const value = context.parsed.x || 0;
            return `${label}: ${value}%`;
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 100,
        ticks: {
          // eslint-disable-next-line func-names
          callback: function (value) {
            return value + '%';
          },
          color: chartColors.textSecondary,
        },
        grid: {
          color: chartColors.gridColor,
        },
      },
      y: {
        ticks: {
          color: chartColors.textSecondary,
        },
        grid: {
          display: false,
        },
      },
    },
  };
});

const hasData = computed(() => {
  return chartData.value.labels.length > 0;
});
</script>

<template>
  <div
    class="bg-n-solid-2 rounded-xl p-4 shadow outline-1 outline outline-n-container w-full max-w-full"
  >
    <h3 class="text-lg font-medium text-n-slate-12 mb-4 text-center">
      {{ $t('REPORT.AGENT_PERFORMANCE_CHART_TITLE') }}
    </h3>
    <div v-if="hasData" class="h-80 w-full">
      <BarChart :collection="chartData" :chart-options="chartOptions" />
    </div>
    <div v-else class="flex items-center justify-center h-80">
      <span class="text-sm text-slate-600">
        {{ $t('REPORT.NO_ENOUGH_DATA') }}
      </span>
    </div>
  </div>
</template>
